"use strict";

import dispatcher from "./../../dispatcher";
import sessionStore from "./../../store/session";
import dataTypeStore from "./../../store/dataTypes";
import vehicleStore from "./../../store/vehicles";
import selectedVehicleStore from "./../../store/selectedVehicle";
import vehicleStateStore from "./../../store/vehicleStates";
import pinnedVehicleStore from "./../../store/pinnedVehicles";
import focusedVehicleStore from "./../../store/focusedVehicle";
import autoZoomStore from "./../../store/autoZoom";

import zIndex from "./zIndex";
import VehicleBubble from "./VehicleBubble";

import {positionToLatLng} from "./../../utils/geo";
import * as vehicleUtils from "./../../utils/vehicle";

const FOCUS_ZOOM = 16;

export default class BubbleController {
	
	constructor(map) {
		this.map = map;
		this.initBubbles();
		this.setListeners();
		this.canTurnOffAutoZoom = true;
	}
	
	initBubbles() {
		this.bubbles = {};
		this.removeAllBubbles();
		this.addBubbles(pinnedVehicleStore.getAll());
	}
	
	setListeners() {
		
		google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => dispatcher.dispatch('toggleAutoZoom'));
		
		google.maps.event.addListener(this.map, 'drag', () => dispatcher.dispatch('turnOffAutoZoom'));
		google.maps.event.addListener(this.map, 'zoom_changed', () => {
			if (this.canTurnOffAutoZoom) dispatcher.dispatch('turnOffAutoZoom');
		});
		google.maps.event.addListener(this.map, 'bounds_changed', () => this.updateVisibilities());
		google.maps.event.addListener(this.map, 'click', () => dispatcher.dispatch('selectVehicle', null));
		
		pinnedVehicleStore.on('set', vehicleIds => this.addBubbles(vehicleIds));
		pinnedVehicleStore.on('insert', vehicleIds => this.addBubbles(vehicleIds));
		pinnedVehicleStore.on('remove', vehicleIds => this.removeBubbles(vehicleIds));
		vehicleStateStore.on('update', vehicleState => {
			this.updateMovingState(vehicleState);
			this.updateAutoZoom();
		});
		focusedVehicleStore.on('change', vehicleId => this.updateFocus(vehicleId));
		selectedVehicleStore.on('change', vehicleId => this.updateSelections(vehicleId));
		autoZoomStore.on('change', value => this.updateAutoZoom());
		
		vehicleStore.on('update', vehicle => this.updateName(vehicle._id))
	}
	
	addBubbles(vehicleIds) {
		if (!vehicleIds)
			return;
		
		vehicleIds.forEach(vehicleId => {
			if (vehicleId in this.bubbles)
				return;
			
			const position = vehicleUtils.getPositionOfVehicle(vehicleId);
			
			if(!position){
				return;
			}
			
			this.bubbles[vehicleId] = new VehicleBubble({
				map:			this.map,
				position:		position,
				visible:		this.getVisibilityOfVehicle(vehicleId),
				selected:		vehicleUtils.getSelectionOfVehicle(vehicleId),
				name:			vehicleUtils.getNameOfVehicle(vehicleId),
				zIndex:			getZIndexOfVehicle(vehicleId),
				movingState:	vehicleUtils.getMovingStateOfVehicle(vehicleId),
				onClick:		() => dispatcher.dispatch('selectVehicle', vehicleId)
			});
		});
		
		this.updateAutoZoom();
	}
	
	removeBubbles(vehicleIds) {
		if (!vehicleIds)
			return;
		
		vehicleIds.forEach(vehicleId => {
			if (!this.bubbles[vehicleId])
				return;
			
			this.bubbles[vehicleId].close();
			delete this.bubbles[vehicleId];
		});
		
		this.updateAutoZoom();
	}
	
	removeAllBubbles() {
		this.removeBubbles(Object.keys(this.bubbles));
	}
	
	getVisibilityOfVehicle(vehicleId) {
		const position = vehicleUtils.getPositionOfVehicle(vehicleId);
		if (!position)
			return false;
		const bounds = this.map.getBounds();
		if (!bounds)
			return false;
		return bounds.contains(position);
	}
	
	updateVisibilities() {
		for (let vehicleId in this.bubbles) {
			this.bubbles[vehicleId].update({visible: this.getVisibilityOfVehicle(vehicleId)});
		}
	}
	
	updateSelections(vehicleId) {
		for (let vehicleId in this.bubbles) {
			this.bubbles[vehicleId].update({selected: selectedVehicleStore.get() == vehicleId});
		}
		this.updateZIndexes();
	}
	
	updateZIndexes() {
		for (let vehicleId in this.bubbles) {
			this.bubbles[vehicleId].update({zIndex: getZIndexOfVehicle(vehicleId)});
		}
	}
	
	updateName(vehicleId) {
		if (!vehicleId || !this.bubbles[vehicleId])
			return;
		this.bubbles[vehicleId].update({name: vehicleUtils.getNameOfVehicle(vehicleId)});
	}
	
	updateMovingState(vehicleState) {
		const vehicleId = vehicleState.carrier;
		if (!this.bubbles[vehicleId])
			return;
		this.bubbles[vehicleId].update({
			position:		vehicleUtils.getPositionOfVehicle(vehicleId),
			movingState:	vehicleUtils.getMovingStateOfVehicle(vehicleId),
			zIndex:			getZIndexOfVehicle(vehicleId),
			selected:		selectedVehicleStore.get() == vehicleId
		});
	}
	
	updateFocus(vehicleId) {
		if (!vehicleId)
			return;
		const position = vehicleUtils.getPositionOfVehicle(vehicleId);
		if (!position)
			return;
		dispatcher.dispatch('setMap', {
			center:	position.toJSON(),
			zoom:	FOCUS_ZOOM
		});
		
		this.updateZIndexes();
	}
	
	updateAutoZoom() {
		if (!autoZoomStore.get())
			return;
		
		const bounds = vehicleUtils.getBoundsOfPinnedVehicles();
		if (!bounds || bounds.isEmpty())
			return;
		
		this.canTurnOffAutoZoom = false;
		
		dispatcher.dispatch('setMap', {bounds: bounds.toJSON()});
		
		if (this.map.getZoom() > FOCUS_ZOOM) {
			dispatcher.dispatch('setMap', {
				center: bounds.getCenter().toJSON(),
				zoom:	FOCUS_ZOOM
			});
		}
		setTimeout(() => this.canTurnOffAutoZoom = true);
	}
	
	refreshAllBubbles() {
		Object.keys(this.bubbles).forEach(
			vehicleId => this.bubbles[vehicleId].refresh()
		);
	}
}

function getZIndexOfVehicle(vehicleId) {
	const movingState = vehicleUtils.getMovingStateOfVehicle(vehicleId);
	const selectedVehicle = selectedVehicleStore.get();
	const focusedVehicle = focusedVehicleStore.get();
	
	if (selectedVehicle == vehicleId) return zIndex.SELECTED;
	else if (focusedVehicle == vehicleId) return zIndex.FOCUSED;
	else if (movingState == "onIgnition") return zIndex.IGNITION_ON;
	else if (movingState == "moving") return zIndex.MOVING;
	else return zIndex.IGNITION_OFF;
}


// WEBPACK FOOTER //
// ./src/view/Map/BubbleController.js